/**
 * Client-side upload — using Firebase Storage SDK directly.
 * Bypasses Cloud Functions for uploads — more reliable, no IAM issues.
 * Uses uploadBytesResumable for progress tracking and resume support.
 */

import { ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "@/config/firebase";

// ─── Types ─────────────────────────────────────────────────────

export interface UploadProgress {
  file_name: string;
  bytes_uploaded: number;
  total_bytes: number;
  percent: number;
  status: "preparing" | "uploading" | "complete" | "error";
  error?: string;
  storage_path?: string;
}

type ProgressCallback = (progress: UploadProgress) => void;

// ─── Upload File ───────────────────────────────────────────────

export async function uploadFile(
  file: File,
  projectId: string,
  fileType: "audio" | "text",
  onProgress?: ProgressCallback,
): Promise<string> {
  const progress: UploadProgress = {
    file_name: file.name,
    bytes_uploaded: 0,
    total_bytes: file.size,
    percent: 0,
    status: "preparing",
  };

  onProgress?.(progress);

  // Build storage path per spec §3.3
  const subfolder = fileType === "audio" ? "audio" : "documents";
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `storyforge-uploads/${projectId}/${subfolder}/${timestamp}_${safeName}`;

  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
  });

  return new Promise<string>((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        progress.bytes_uploaded = snapshot.bytesTransferred;
        progress.total_bytes = snapshot.totalBytes;
        progress.percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        progress.status = "uploading";
        onProgress?.(progress);
      },
      (error) => {
        progress.status = "error";
        progress.error = error.message ?? "Upload failed";
        onProgress?.(progress);
        reject(error);
      },
      async () => {
        progress.status = "complete";
        progress.percent = 100;
        progress.bytes_uploaded = progress.total_bytes;
        progress.storage_path = storagePath;
        onProgress?.(progress);
        resolve(storagePath);
      },
    );
  });
}

// ─── Upload Multiple Files ─────────────────────────────────────

export async function uploadFiles(
  files: File[],
  projectId: string,
  fileType: "audio" | "text",
  onProgress?: (fileIndex: number, progress: UploadProgress) => void,
): Promise<string[]> {
  const storagePaths: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const path = await uploadFile(
      files[i],
      projectId,
      fileType,
      (progress) => onProgress?.(i, progress),
    );
    storagePaths.push(path);
  }

  return storagePaths;
}

// ─── Format Helpers ────────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}
